import { useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, X, ArrowLeft, ArrowRight, Upload, Check, Loader2, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { readWorkbook, detectLayout, parseItems, parsePayments, lineTotal, groupEntries, collectIssues, explainDifference, findOrphanTotals, refreshDiscCheck } from "@/lib/ledgerImport";
import { confirm } from "@/components/ConfirmDialog";
import StepContact from "./importSteps/StepContact";
import StepMapping from "./importSteps/StepMapping";
import StepPreview from "./importSteps/StepPreview";
import StepDone, { BulkSummary } from "./importSteps/StepDone";
import QueueStrip from "./importSteps/QueueStrip";

const STEPS = [["Cari Bilgileri", "kime aktarılacak"], ["Excel & Sütunlar", "dosya + eşleme"], ["Önizleme", "kontrol & düzelt"], ["Sonuç", "aktarım özeti"]];
export const blankImportContact = () => ({ title: "", type: "musteri", phone: "", tax_no: "", address: "", vat_exempt: true, parent_id: "", new_parent_title: "" });
const norm = (s) => String(s || "").trim().toLocaleLowerCase("tr");
/** "KAAN CARİ.xlsx" → "KAAN" (uzantı ve CARİ/HESAP/EKSTRE gibi son ekler atılır) */
export const titleFromFileName = (name) => String(name || "").replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").trim().replace(/\s+(car[iİıI]|car[iİıI] hesap|hesap|hesab[ıi]|ekstre|ekstresi|defter|defteri)$/i, "").trim().toLocaleUpperCase("tr");

/** Excel'den cari aktarma sihirbazı — tam ekran, 4 adım; birden fazla dosya seçilirse sırayla (kuyruk) aktarılır. */
export default function ImportLedgerWizard({ open, onOpenChange, contacts = [], onDone, onOpenContact }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("new");
  const [contact, setContact] = useState(blankImportContact);
  const [existingId, setExistingId] = useState("");
  const [opening, setOpening] = useState({ amount: "", date: "" });
  const [payMethod, setPayMethod] = useState("nakit");
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [layout, setLayout] = useState(null);
  const [discountMode, setDiscountMode] = useState("auto");
  const [items, setItems] = useState([]);
  const [pays, setPays] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState([]);       // [{ file, name, status: pending|done|skipped, result }]
  const [qIndex, setQIndex] = useState(-1);
  const [bulkDone, setBulkDone] = useState(false);

  const existing = contacts.find((c) => c.id === existingId);
  const vatExempt = mode === "existing" ? !!existing?.vat_exempt : !!contact.vat_exempt;
  const grid = sheets[sheetIdx]?.grid || [];
  const inQueue = queue.length > 1;
  const pendingAfter = queue.filter((q, i) => i !== qIndex && q.status === "pending").length;

  const resetFile = () => {
    setStep(1); setMode("new"); setContact(blankImportContact()); setExistingId(""); setOpening({ amount: "", date: "" });
    setFile(null); setSheets([]); setSheetIdx(0); setLayout(null); setItems([]); setPays([]); setOrphans([]); setResult(null);
  };
  const reset = () => { resetFile(); setDiscountMode("auto"); setQueue([]); setQIndex(-1); setBulkDone(false); };
  const close = async () => {
    if (pendingAfter > 0 && !bulkDone) {
      const ok = await confirm({ title: "Kuyrukta bekleyen dosyalar var", description: `${pendingAfter} Excel dosyası henüz aktarılmadı. Sihirbazı kapatırsanız kuyruk silinir (aktarılanlar kalır).`, confirmText: "Yine de kapat", warning: true });
      if (!ok) return;
    }
    onOpenChange(false); setTimeout(reset, 300);
  };

  /** Dosyayı oku, düzeni tanı; ünvanı dosya adından öner, aynı adlı cari varsa mevcut cariye aktarımı seç. */
  const loadFile = async (f, { prefill = true, fresh = false } = {}) => {
    setLoading(true);
    try {
      const sh = await readWorkbook(f);
      if (!sh.length) throw new Error("Sayfa bulunamadı");
      setFile(f); setSheets(sh); setSheetIdx(0); setLayout(detectLayout(sh[0].grid));
      if (prefill) {
        const t = titleFromFileName(f.name);
        const match = contacts.find((c) => norm(c.title) === norm(t));
        if (match) { setMode("existing"); setExistingId(match.id); toast.info(`"${match.title}" adlı cari zaten var — mevcut cariye aktarım seçildi`); }
        else if (fresh || (mode === "new" && !contact.title.trim())) setContact((c) => ({ ...c, title: t }));
      }
      toast.success(`${f.name} okundu · ${sh.length} sayfa`);
      return true;
    } catch (e) { toast.error(`Excel okunamadı: ${e.message || e}`); return false; }
    finally { setLoading(false); }
  };

  /** Dropzone / dosya seçici: tek dosya → normal akış; birden fazla → kuyruk (ilki hemen yüklenir). */
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.xls[xm]?$/i.test(f.name));
    if (!files.length) return toast.error("Excel dosyası (.xlsx / .xls) seçin");
    if (queue.length) {
      setQueue((q) => { const nq = [...q]; if (qIndex >= 0) nq[qIndex] = { ...nq[qIndex], file: files[0], name: files[0].name }; return [...nq, ...files.slice(1).map((f) => ({ file: f, name: f.name, status: "pending", result: null }))]; });
    } else if (files.length > 1) {
      setQueue(files.map((f) => ({ file: f, name: f.name, status: "pending", result: null }))); setQIndex(0);
      toast.info(`${files.length} dosya kuyruğa alındı — sırayla, her biri önizlenerek aktarılacak`);
    }
    await loadFile(files[0]);
  };

  const openQueued = async (i) => {
    resetFile(); setQIndex(i);
    await loadFile(queue[i].file, { prefill: true, fresh: true });
  };
  const markCurrent = (status, res = null) => setQueue((q) => q.map((x, i) => (i === qIndex ? { ...x, status, result: res } : x)));
  const nextFile = async () => {
    const ni = queue.findIndex((q, i) => i > qIndex && q.status === "pending");
    if (ni < 0) { setBulkDone(true); setStep(4); return; }
    await openQueued(ni);
  };
  const skipFile = async () => { markCurrent("skipped"); await nextFile(); };

  const canNext = () => {
    if (step === 1) return mode === "existing" ? !!existingId : !!contact.title.trim();
    if (step === 2) return !!layout && Object.values(layout.map).includes("name");
    if (step === 3) return items.some((i) => i.include) || pays.some((p) => p.include) || Number(opening.amount) !== 0;
    return false;
  };
  const next = () => {
    if (step === 1 && mode === "new" && contacts.some((c) => norm(c.title) === norm(contact.title))) {
      return toast.error("Bu ünvan zaten kayıtlı — 'Mevcut cariye aktar' seçin veya ünvanı değiştirin");
    }
    if (step === 2) {
      const lay = refreshDiscCheck(grid, layout);
      setLayout(lay); setItems(parseItems(grid, lay, { discountMode })); setPays(parsePayments(grid, lay)); setOrphans(findOrphanTotals(grid, lay));
    }
    setStep((s) => s + 1);
  };

  const totals = useMemo(() => {
    const inc = items.filter((i) => i.include);
    const net = inc.reduce((s, i) => s + lineTotal(i), 0) + (Number(opening.amount) || 0);
    const kdv = vatExempt ? 0 : net * 0.2;
    const paid = pays.filter((p) => p.include).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return { count: inc.length, net, kdv, total: net + kdv, paid, balance: net + kdv - paid };
  }, [items, pays, opening.amount, vatExempt]);

  const submit = async () => {
    const inc = items.filter((i) => i.include && String(i.name || "").trim());
    const diff = explainDifference(items, pays, { summary: layout?.summary, totals, opening: opening.amount, orphans, vatExempt });
    const issues = collectIssues(items, pays, { summary: layout?.summary, totals, vatExempt, returns: layout?.returns, discCheck: layout?.discCheck, orphans, diff }).filter((x) => x.sev !== "info");
    if (issues.length) {
      const errs = issues.filter((x) => x.sev === "error"), warns = issues.filter((x) => x.sev === "warn");
      const ok = await confirm({
        title: errs.length ? "Şüpheli / anlaşılmayan veriler var — yine de aktarılsın mı?" : "Eksik alanlar var — yine de aktarılsın mı?",
        description: [
          ...errs.map((x) => `⛔ ${x.text}` + (x.rows.length ? `\n     ${x.rows.slice(0, 4).map((r) => (r.excelRow ? `Satır ${r.excelRow}: ` : "") + r.label).join(" · ")}${x.rows.length > 4 ? ` · +${x.rows.length - 4}` : ""}` : "")),
          ...warns.map((x) => `• ${x.text}`),
          "",
          errs.length ? "Şüpheli satırları önizlemede düzeltmeniz veya aktarım dışı bırakmanız önerilir. Devam ederseniz veriler olduğu gibi (eksikler BOŞ) aktarılır." : "Devam ederseniz eksik alanlar BOŞ bırakılır; tarihi olmayan kalemler Excel'deki satır sırasına göre deftere işlenir.",
        ].join("\n"),
        confirmText: errs.length ? "Kontrol ettim, aktar" : "Boş bırak ve aktar", warning: true,
      });
      if (!ok) return;
    }
    const mapItem = (i) => ({ name: i.name, code: i.code || "", unit: i.unit || "", quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0, discount_rate: Number(i.discount_rate) || 0, description: "" });
    const payload = {
      contact: mode === "existing" ? { id: existingId } : { ...contact, parent_id: contact.parent_id || null },
      entries: groupEntries(inc, mapItem),
      payments: pays.filter((p) => p.include && Number(p.amount) > 0).map((p) => ({ date: p.date || "", amount: Number(p.amount), method: payMethod, note: p.note ? `Excel aktarımı · ${p.note}` : "Excel aktarımı" })),
      opening_balance: Number(opening.amount) || 0, opening_date: opening.date || "", source_file: file?.name || "",
    };
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/import-ledger", payload);
      setResult(data); setStep(4);
      if (inQueue) markCurrent("done", data);
      toast.success(`${data.contact.title} aktarıldı`);
      onDone && onDone(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const nextQueued = queue.find((q, i) => i > qIndex && q.status === "pending");
  const footerInfo = () => {
    if (inQueue) return <>Dosya <b className="text-foreground">{qIndex + 1} / {queue.length}</b> · {queue[qIndex]?.name}{step === 3 ? <> · Bakiye <b className="text-foreground tabular-nums">{totals.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</b></> : null}</>;
    if (step === 3) return <>{totals.count} kalem · {pays.filter((p) => p.include).length} ödeme · Bakiye <b className="text-foreground tabular-nums">{totals.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</b></>;
    if (step === 2 && file) return <>{file.name}{sheets.length > 1 ? ` · ${sheets[sheetIdx]?.name}` : ""}</>;
    return mode === "existing" ? (existing?.title || "Mevcut cari seçin") : (contact.title || "Yeni cari");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-none w-[98vw] h-[96vh] max-h-[96vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="import-wizard">
        <div className="relative shrink-0 px-5 py-3.5 bg-[#0B192C] text-white overflow-hidden">
          <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-primary/30 blur-3xl" />
          <span className="pointer-events-none absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-primary via-orange-300 to-primary" />
          <div className="relative flex flex-wrap items-center gap-4">
            <span className="h-9 w-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center"><FileSpreadsheet className="h-4.5 w-4.5 text-[#ffb27a]" /></span>
            <div className="min-w-0">
              <DialogTitle className="font-head text-lg sm:text-xl font-black tracking-tight leading-tight">{inQueue ? "Toplu Excel Aktarımı" : "Excel'den Cari Aktar"}</DialogTitle>
              <p className="text-[11px] text-slate-300 mt-0.5 hidden sm:block">{inQueue ? "Dosyalar sırayla işlenir — her biri için cari bilgileri, sütun eşleme ve önizleme onayınızı bekler." : "Birden fazla Excel seçerseniz dosyalar sırayla, her biri önizlenerek aktarılır."}</p>
            </div>
            <ol className="ml-auto flex items-center gap-1.5 sm:gap-3" data-testid="import-steps">
              {STEPS.map(([t, h], i) => {
                const n = i + 1, on = n === step, done = n < step;
                return (
                  <li key={t} className={cn("flex items-center gap-2 rounded-full px-2.5 py-1 border transition-colors", on ? "bg-primary border-primary text-white" : done ? "bg-white/10 border-white/10 text-emerald-200" : "border-white/10 text-slate-400")} data-testid={`import-step-${n}`}>
                    <span className={cn("h-5 w-5 rounded-full text-[11px] font-bold flex items-center justify-center", on ? "bg-white text-primary" : done ? "bg-emerald-400 text-[#0B192C]" : "bg-white/10")}>{done ? <Check className="h-3 w-3" /> : n}</span>
                    <span className="hidden md:block leading-none"><span className="block text-xs font-bold">{t}</span><span className="block text-[10px] opacity-80">{h}</span></span>
                  </li>
                );
              })}
            </ol>
            <button type="button" onClick={close} className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="import-close-x" title="Kapat"><X className="h-4 w-4" /></button>
          </div>
        </div>
        {inQueue && <QueueStrip queue={queue} current={qIndex} onJump={(i) => queue[i].status === "pending" && i !== qIndex && openQueued(i)} />}

        <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50/70 p-4 sm:p-5">
          {step === 1 && <StepContact mode={mode} setMode={setMode} contact={contact} setContact={setContact} existingId={existingId} setExistingId={setExistingId} contacts={contacts} opening={opening} setOpening={setOpening} payMethod={payMethod} setPayMethod={setPayMethod} file={file} onFiles={handleFiles} loading={loading} />}
          {step === 2 && <StepMapping file={file} sheets={sheets} sheetIdx={sheetIdx} setSheetIdx={setSheetIdx} layout={layout} setLayout={setLayout} discountMode={discountMode} setDiscountMode={setDiscountMode} onFiles={handleFiles} loading={loading} />}
          {step === 3 && <StepPreview items={items} setItems={setItems} pays={pays} setPays={setPays} layout={layout} orphans={orphans} totals={totals} vatExempt={vatExempt} mode={mode} contact={contact} setContact={setContact} existing={existing} opening={opening} setOpening={setOpening} />}
          {step === 4 && bulkDone && <BulkSummary queue={queue} onClose={close} onOpen={(c) => { onOpenContact && onOpenContact(c); close(); }} />}
          {step === 4 && !bulkDone && result && <StepDone result={result} onAgain={reset} onOpen={() => { onOpenContact && onOpenContact(result.contact); close(); }} onClose={close} nextName={inQueue ? nextQueued?.name : null} onNext={inQueue ? nextFile : null} queuePos={inQueue ? `${qIndex + 1} / ${queue.length}` : null} />}
        </div>

        {(step < 4) && (
          <div className="shrink-0 border-t bg-card px-4 sm:px-5 py-3 flex flex-wrap items-center gap-3">
            <div className="text-xs text-muted-foreground hidden sm:block" data-testid="import-footer-info">{footerInfo()}</div>
            <div className="ml-auto flex items-center gap-2">
              {inQueue && <Button variant="ghost" onClick={skipFile} disabled={busy || loading} data-testid="import-skip-file"><SkipForward className="h-4 w-4 mr-1.5" />Bu dosyayı atla</Button>}
              {step > 1 && <Button variant="outline" onClick={() => setStep((s) => s - 1)} data-testid="import-back"><ArrowLeft className="h-4 w-4 mr-1.5" />Geri</Button>}
              {step < 3 && <Button onClick={next} disabled={!canNext() || loading} className="min-w-[150px]" data-testid="import-next">Devam<ArrowRight className="h-4 w-4 ml-1.5" /></Button>}
              {step === 3 && <Button onClick={submit} disabled={!canNext() || busy} className="min-w-[190px]" data-testid="import-submit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Sisteme Aktar</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
